import React, { Dispatch } from "react";
import { RouteComponentProps } from "react-router-dom";
import { connect } from "react-redux";
import { showSnack } from "react-redux-snackbar";
import { Container, Button, Alert } from "reactstrap";
import { Icon } from "@cadmakers-ui/icons";
import iconStyles from "../../../Common/Styles/Icons.module.scss";
import { FeatureRequest, BugReport, Feedback, ResourceRequest } from "../../Models/Feedback";
import {
  IAppState,
  ApiRequest,
  appPaths,
  ErrorDisplay,
  ForbiddenDisplay,
  LoadingState,
  LoadingDisplay
} from "../../../Common";
import {
  getFeedback,
  setFeedback,
  resetLoading,
  setLoading,
  resetLoaded
} from "../../Actions/FeedbackEditActions";
import { FeedbackCategory, FeedbackParent } from "../../Constants/feedbackEnums";
import { endpoints } from "../..";
import { resetLoaded as viewResetLoaded } from "../../Actions/FeedbackDetailsActions";
import { ResponseResult } from "../../../Common/Models/ResponseResult";
import EditFeatureRequestForm from "./EditFeatureRequestForm";
import EditBugReportForm from "./EditBugReportForm";
import EditResourceRequestForm from "./EditResourceRequestForm";
import ResponseHandler from "../../../App/ResponseHandler";
import { Application } from "../../../App/Models/Application";
import { Topic } from "../../../Topic/Models/Topic";
import { updateTopics, getTopics } from "../../../Startup/Actions/StartupActions";
import { Company } from "../../../Company/Models/Company";
import { feedbackService } from "../../Services";
import { UpdateFeedback } from "../../Models/UpdateFeedback";
import MoveFeedbackModal from "../Common/MoveFeedbackModal";
import { feedbackCategoryService } from "../../Services/FeedbackCategoryService";
import styles from "../../Styles/EditFeedbackContainer.module.scss";

type TParams = {
  code: string;
};

type StateProps = {
  feedback: FeatureRequest | BugReport | ResourceRequest | null;
  contentIsLoaded: boolean;
  isLoading: boolean;
  hasError: boolean;
  errorCode: number | null;
  errorDescription: string;
  appLoadingState: LoadingState;
  applications: Application[];
  topics: Topic[];
  company?: Company;
};

type DispatchProps = {
  getFeedback: (code: string) => void;
  setFeedback: (feedback: Feedback) => void;
  resetLoaded: () => void;
  resetLoading: () => void;
  setLoading: () => void;
  viewResetLoaded: () => void;
  showSnack: (uniqueId: any, args: any) => void;
  updateTopics: (topics: Topic[]) => void;
  getTopics: (shortname: string) => void;
};

type State = {
  canSubmit: boolean;
  hasMoved: boolean;
  selectedParent?: FeedbackParent;
  selectedApp?: Application;
  selectedTopic?: Topic;
  selectedCategory?: FeedbackCategory;
  feedback?: Feedback;
  didSetFeedback: boolean;
  isMoveFeedbackModalOpen: boolean;
};

const mapStateToProps = (state: IAppState): StateProps => ({
  feedback: state.feedbackEdit.feedback,
  contentIsLoaded: state.feedbackEdit.contentIsLoaded,
  isLoading: state.feedbackEdit.isLoading,
  hasError: state.feedbackEdit.hasError,
  errorCode: state.feedbackEdit.errorCode,
  errorDescription: state.feedbackEdit.errorDescription,
  appLoadingState: state.app.appsLoadingState,
  applications: state.app.applications,
  topics: state.app.topics,
  company: state.app.company
});

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  getFeedback: (code: string) => dispatch(getFeedback(code)),
  setFeedback: (feedback: Feedback) => dispatch(setFeedback(feedback)),
  resetLoaded: () => dispatch(resetLoaded()),
  resetLoading: () => dispatch(resetLoading()),
  setLoading: () => dispatch(setLoading()),
  viewResetLoaded: () => dispatch(viewResetLoaded()),
  showSnack: (uniqueId: any, args: any) => dispatch(showSnack(uniqueId, args)),
  updateTopics: (topics: Topic[]) => dispatch(updateTopics(topics)),
  getTopics: (shortname: string) => dispatch(getTopics(shortname))
});

class EditFeedbackContainer extends React.Component<
  RouteComponentProps<TParams> & StateProps & DispatchProps,
  State
> {
  constructor(props: RouteComponentProps<TParams> & StateProps & DispatchProps) {
    super(props);

    this.state = {
      canSubmit: true,
      hasMoved: false,
      didSetFeedback: false,
      isMoveFeedbackModalOpen: false
    };

    this.toggleMoveFeedbackModal = this.toggleMoveFeedbackModal.bind(this);
  }

  componentDidMount = () => {
    if (!this.props.isLoading) {
      if (!this.props.contentIsLoaded) {
        this.props.setLoading();
        this.props.getFeedback(this.props.match.params.code);
      } else {
        const feedbackCode = (this.props.feedback as Feedback).code;
        const paramsCode = this.props.match.params.code;

        if (feedbackCode !== paramsCode) {
          this.props.setLoading();
          this.props.getFeedback(this.props.match.params.code);
        }
      }
    }
  };

  componentWillUnmount = () => {
    this.props.resetLoading();
  };

  componentDidUpdate = () => {
    if (!this.props.isLoading && this.props.contentIsLoaded && !this.state.didSetFeedback) {
      this.setState({
        feedback: this.props.feedback as Feedback,
        didSetFeedback: true
      });

      if (!this.state.hasMoved) {
        this.setState({
          selectedApp: (this.props.feedback as Feedback).application,
          selectedCategory: (this.props.feedback as Feedback).category,
          selectedParent: (this.props.feedback as Feedback).parent,
          selectedTopic: (this.props.feedback as Feedback).topic
        });
      }
    }
  };

  assignMoveProperties = (feedback: Feedback) => {
    return {
      ...feedback,
      parent: this.state.selectedParent as FeedbackParent,
      category: this.state.selectedCategory as FeedbackCategory,
      application: this.state.selectedApp as Application,
      topic: this.state.selectedTopic as Topic
    };
  };

  onSaveEdit = (feedback: Feedback) => {
    let movedFeedback = feedback;

    if (this.state.hasMoved) {
      movedFeedback = this.assignMoveProperties(feedback);
    }

    this.updateFeedback(movedFeedback);
  };

  updateFeedback = (feedback: Feedback) => {
    const updateFeedback = feedbackService.createSubmitModel(feedback);
    this.setState({ canSubmit: false });
    let allowRedirect = false;
    ApiRequest.fromPath(endpoints.feedback.update((this.props.feedback as Feedback).code))
      .withAuthHeader()
      .withJson(updateFeedback)
      .put()
      .then(async (response: Response) => {
        await ResponseHandler.handle(
          response,
          () => {
            this.props.resetLoaded();
            allowRedirect = true;
          },
          (result: ResponseResult) => {
            this.showErrorSnackbar(
              response.status,
              result.error.message,
              "Failed to update feedback"
            );
          },
          () => {
            this.showErrorSnackbar(
              response.status,
              "An unknown error has occured",
              "Failed to update feedback"
            );
          }
        );
      })
      .finally(() => {
        this.setState({
          canSubmit: true
        });
        if (allowRedirect) {
          this.props.viewResetLoaded();
          this.redirectToPost(feedback as Feedback);
        }
      });
  };

  redirectToPost = (feedback: Feedback) => {
    this.props.history.push(feedbackService.getViewUrl(feedback));
  };

  showErrorSnackbar = (errorCode: number, errorDescription: string, reason: string) => {
    this.props.showSnack("EDIT_FEEDBACK", {
      label: `${reason}: ${errorDescription} (Error Code ${errorCode})`,
      timeout: 4000
    });
  };

  showMessageSnackbar = (message: string) => {
    this.props.showSnack("EDIT_FEEDBACK", {
      label: message,
      timeout: 4000
    });
  };

  onDiscardEdit = () => {
    this.props.history.push(feedbackService.getViewUrl(this.props.feedback as Feedback));
  };

  getFormProps = () => {
    return {
      canSubmit: this.state.canSubmit,
      onSubmit: this.onSaveEdit,
      onDiscard: this.onDiscardEdit,
      feedback: this.props.feedback as Feedback
    };
  };

  getReportForm = (category: FeedbackCategory) => {
    switch (category) {
      case FeedbackCategory.FeatureRequest: {
        return <EditFeatureRequestForm {...this.getFormProps()} />;
      }
      case FeedbackCategory.BugReport: {
        return <EditBugReportForm {...this.getFormProps()} />;
      }
      case FeedbackCategory.ResourceRequest: {
        return <EditResourceRequestForm {...this.getFormProps()} />;
      }
      default:
        throw new Error(`Invalid feedback category "${category}"`);
    }
  };

  onMove = (
    parent: FeedbackParent,
    category: FeedbackCategory,
    app?: Application,
    topic?: Topic
  ) => {
    this.setState({
      hasMoved: true,
      selectedParent: parent,
      selectedCategory: category,
      selectedApp: app,
      selectedTopic: topic
    });
  };

  hasMoved = () => {
    const feedback = this.props.feedback as Feedback;
    if (
      feedback.parent === this.state.selectedParent &&
      feedback.category === this.state.selectedCategory &&
      ((!!feedback.application &&
        !!this.state.selectedApp &&
        feedback.application.rolePrefix === this.state.selectedApp.rolePrefix) ||
        (!!feedback.topic &&
          !!this.state.selectedTopic &&
          feedback.topic.code === this.state.selectedTopic.code))
    ) {
      return false;
    }

    return true;
  };

  undoMove = () => {
    if (!this.props.feedback) {
      return;
    }

    this.setState(
      {
        selectedParent: this.props.feedback.parent,
        selectedCategory: this.props.feedback.category,
        selectedApp: this.props.feedback.application,
        selectedTopic: this.props.feedback.topic,
        hasMoved: false
      },
      () => this.showMessageSnackbar("Move has been reverted")
    );
  };

  createParentChangedMessage = () => {
    return !!this.props.feedback && this.props.feedback.parent !== this.state.selectedParent ? (
      <p>
        <span className={styles.bold}>Parent: </span>
        {this.props.feedback.parent} <span className={styles.bold}>🠚</span>{" "}
        {this.state.selectedParent}
      </p>
    ) : null;
  };

  createCategoryChangedMessage = () => {
    return !!this.props.feedback && this.props.feedback.category !== this.state.selectedCategory ? (
      <p>
        <span className={styles.bold}>Category: </span>
        {feedbackCategoryService.getDisplayNameForCategory(this.props.feedback.category)}{" "}
        <span className={styles.bold}>🠚</span>{" "}
        {feedbackCategoryService.getDisplayNameForCategory(
          this.state.selectedCategory as FeedbackCategory
        )}
      </p>
    ) : null;
  };

  createAppChangedMessage = () => {
    return !!this.props.feedback && this.props.feedback.application !== this.state.selectedApp ? (
      <p>
        <span className={styles.bold}>Application: </span>
        {this.props.feedback.application ? this.props.feedback.application.name : "None"}{" "}
        <span className={styles.bold}>🠚</span>{" "}
        {this.state.selectedApp ? this.state.selectedApp.name : "None"}
      </p>
    ) : null;
  };

  createTopicChangedMessage = () => {
    return !!this.props.feedback && this.props.feedback.topic !== this.state.selectedTopic ? (
      <p>
        <span className={styles.bold}>Topic: </span>
        {this.props.feedback.topic ? this.props.feedback.topic.name : "None"}{" "}
        <span className={styles.bold}>🠚</span>{" "}
        {this.state.selectedTopic ? this.state.selectedTopic.name : "None"}
      </p>
    ) : null;
  };

  createMovedMessage = () => {
    if (!this.props.feedback || !this.hasMoved()) {
      return null;
    }

    return (
      <Alert color="info" className={styles.alertMessage}>
        <small>
          <span className={styles.messageContainer}>
            <span className={styles.left}>
              <p>
                <span style={{ fontWeight: "bold" }}>Note:</span> This feedback has been moved. The
                following changes have been made:
              </p>
              {this.createParentChangedMessage()}
              {this.createCategoryChangedMessage()}
              {this.createAppChangedMessage()}
              {this.createTopicChangedMessage()}
            </span>
            <span className={styles.right}>
              <span className={styles.undo} onClick={this.undoMove}>
                Undo
              </span>
            </span>
          </span>
        </small>
      </Alert>
    );
  };

  createCategoryChangedWarning = () => {
    if (!!this.props.feedback && this.props.feedback.category !== this.state.selectedCategory) {
      return (
        <Alert color="danger">
          <small>
            <span className={styles.bold}>Warning: </span>Moving a feedback to a different category
            will reset its state to the start state of that category&apos;s workflow and you will be
            required to enter new values for that category&apos;s fields.
          </small>
        </Alert>
      );
    }

    return null;
  };

  toggleMoveFeedbackModal() {
    this.setState(prevState => ({
      isMoveFeedbackModalOpen: !prevState.isMoveFeedbackModalOpen
    }));
  }

  render() {
    if (this.props.hasError) {
      return (
        <ErrorDisplay
          errorCode={this.props.errorCode as number}
          errorDescription={this.props.errorDescription}
          errorResult="Failed to get feedback"
        />
      );
    }

    if (
      this.props.contentIsLoaded &&
      !this.props.isLoading &&
      this.props.appLoadingState.isLoaded &&
      this.state.didSetFeedback
    ) {
      if (!this.props.feedback || !this.props.feedback.permissions) {
        throw Error("Permissions must be loaded to display this page");
      }

      if (!this.props.feedback.permissions.canEdit) {
        // we do not have edit permission
        return <ForbiddenDisplay description="You do not have permission to edit this feedback" />;
      }

      return (
        <div>
          <Container fluid>
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
              <span style={{ alignItems: "flex-start" }}>
                <h2>Edit Feedback</h2>
              </span>

              {this.props.feedback.permissions.canMove ? (
                <span style={{ marginLeft: "auto" }}>
                  <Button onClick={this.toggleMoveFeedbackModal}>
                    <Icon
                      appName="GeneralLayout"
                      assetName="GENERAL_L_SignOut"
                      className={iconStyles.smallIcon}
                    />{" "}
                    Move
                  </Button>
                  <MoveFeedbackModal
                    feedback={this.assignMoveProperties(this.state.feedback as Feedback)}
                    isModalOpen={this.state.isMoveFeedbackModalOpen}
                    toggleModalCallback={this.toggleMoveFeedbackModal}
                    preventDefaultHanding
                    onOverriddenSave={this.onMove}
                  />
                </span>
              ) : null}
            </div>
            <br />
            <div>
              {this.createCategoryChangedWarning()}
              {this.createMovedMessage()}
            </div>
          </Container>

          {this.getReportForm(this.state.selectedCategory as FeedbackCategory)}
        </div>
      );
    }
    return <LoadingDisplay />;
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(EditFeedbackContainer);
